import type {
	SmartWorkspaceDTO,
	ParameterizedConceptDTO,
	SmartWorkspaceDTOChart,
} from "@eu.mdotm.api/smart-workspaces";
import { chartTypeSortingMode } from "../chart-type";
import { workspaceAdapter } from "../ctx/adapter";
import { WorkspaceChartTypeEnum } from "../spec-gaps";
import { assertWorkspaceInvariants } from "./invariants";
import { sweepUnreferencedPrivateConcepts } from "./reducer";
import { type WorkspaceState, fromWorkspace, resolveConcepts } from "./state";

/**
 * Ingestion: the one place a workspace is allowed to arrive broken.
 *
 * The invariants in `invariants.ts` are enforced by throwing, which is only defensible if something
 * establishes them first — nothing else can, because a document persisted months ago by an older client is
 * not a programming error, it is data. So a DTO is deserialized by the adapter and then **repaired** here,
 * once, and everything downstream may assume it resolves — D0008.
 *
 * Repair is deliberately not silent. Every change is returned as a {@link WorkspaceRepair} and logged in
 * dev, because a repaired document means the user is about to notice something missing, and "the app
 * quietly dropped my chart" must be answerable.
 */
export type WorkspaceRepair = {
	invariant: string;
	/** What was changed, in terms a reader can match against what the user sees. */
	detail: string;
};

export type IngestedWorkspace = { state: WorkspaceState; repairs: WorkspaceRepair[] };

/**
 * DTO → repaired state. The entry point for the whole area.
 *
 * `ctx/adapter.ts` is used exactly as it stands — the versioned preference envelopes, the stored-column
 * reconciliation and the `ColumnState` round trip are real behaviour and are not reimplemented here. This
 * composes with it rather than replacing it: deserialize, then normalize.
 */
export function ingestWorkspace(params?: {
	workspace?: SmartWorkspaceDTO;
	sharedParametrizedConcepts?: ParameterizedConceptDTO[];
}): IngestedWorkspace {
	return repairWorkspaceState(fromWorkspace(workspaceAdapter.toContext(params)));
}

/**
 * Reconstructs a state that satisfies every invariant, and says what it had to change to get there.
 *
 * Order matters: references are resolved before charts are pruned (pruning a series can empty a chart), and
 * the sweep runs last because everything above it can orphan a concept.
 */
export function repairWorkspaceState(state: WorkspaceState): IngestedWorkspace {
	const repairs: WorkspaceRepair[] = [];
	let repaired = dedupePrivateConcepts(state, repairs);
	repaired = dropUnresolvableReferences(repaired, repairs);
	repaired = normalizeCharts(repaired, repairs);

	const swept = sweepUnreferencedPrivateConcepts(repaired);
	for (const entry of repaired.concepts) {
		if (entry.origin === "WORKSPACE" && !swept.concepts.includes(entry)) {
			repairs.push({
				invariant: "noOrphanPrivateConcepts",
				detail: `swept private concept ${entry.concept.uuid} (${entry.concept.label ?? "no label"}), referenced by nothing`,
			});
		}
	}
	repaired = swept;

	if (repairs.length > 0 && import.meta.env.DEV) {
		console.warn(
			`workspace ${state.meta.document?.uuid ?? "(unsaved)"} arrived in an invalid state and was repaired:\n${repairs
				.map((repair) => `  · [${repair.invariant}] ${repair.detail}`)
				.join("\n")}`,
		);
	}

	// Repair has to be total: anything it misses would throw on the user's first click instead, with a stack
	// trace pointing at whichever operation they happened to perform. Paid once per document.
	assertWorkspaceInvariants(repaired, "on ingestion, after repair");

	return { state: repaired, repairs };
}

/** Two private entries for one uuid: the first wins, which is the one the serializer would have kept. */
function dedupePrivateConcepts(state: WorkspaceState, repairs: WorkspaceRepair[]): WorkspaceState {
	const seen = new Set<string>();
	const concepts = state.concepts.filter((entry) => {
		if (entry.origin !== "WORKSPACE") {
			return true;
		}
		if (seen.has(entry.concept.uuid)) {
			repairs.push({
				invariant: "privateConceptUuidsUnique",
				detail: `dropped a duplicate private concept ${entry.concept.uuid}`,
			});
			return false;
		}
		seen.add(entry.concept.uuid);
		return true;
	});

	return concepts.length === state.concepts.length ? state : { ...state, concepts };
}

/**
 * Drops every reference the pool cannot resolve.
 *
 * This is the repair a user is most likely to see, and what they see is a missing column, filter or chart —
 * which is also what they saw before, except that the column rendered as an empty header and the chart
 * rendered its broken state. The difference is that the document now says so.
 */
function dropUnresolvableReferences(state: WorkspaceState, repairs: WorkspaceRepair[]): WorkspaceState {
	const pool = new Set(resolveConcepts(state.concepts).map((concept) => concept.uuid));
	const resolves = (uuid: string | undefined | null): boolean => uuid != null && pool.has(uuid);

	const columns =
		state.content.columns?.filter((column) => {
			if (resolves(column.colId)) {
				return true;
			}
			repairs.push({
				invariant: "conceptReferencesResolve",
				detail: `dropped column ${column.colId} — no concept behind it`,
			});
			return false;
		}) ?? null;

	const filters = state.content.filters.filter((filter) => {
		if (resolves(filter.parameterizedConceptUuid)) {
			return true;
		}
		repairs.push({
			invariant: "conceptReferencesResolve",
			detail: `dropped the filter on ${filter.parameterizedConceptUuid} — no concept behind it`,
		});
		return false;
	});

	const charts = state.content.charts.map((chart) => {
		const series = chart.series.filter((uuid) => {
			if (resolves(uuid)) {
				return true;
			}
			repairs.push({
				invariant: "conceptReferencesResolve",
				detail: `dropped series ${uuid} from chart "${chart.label ?? chart.chartType}" — no concept behind it`,
			});
			return false;
		});

		if (chart.relativeThreshold != null && !resolves(chart.relativeThreshold)) {
			repairs.push({
				invariant: "conceptReferencesResolve",
				detail: `cleared the threshold of chart "${chart.label ?? chart.chartType}" — no concept behind it`,
			});
			return { ...chart, series, relativeThreshold: undefined };
		}

		return series.length === chart.series.length ? chart : { ...chart, series };
	});

	let sort = state.content.sort;
	if (sort != null && !resolves(sort.parameterizedConceptUuid)) {
		repairs.push({
			invariant: "conceptReferencesResolve",
			detail: `cleared the sort on ${sort.parameterizedConceptUuid} — no concept behind it`,
		});
		sort = null;
	}

	return { ...state, content: { ...state.content, columns, filters, charts, sort } };
}

/** The three chart-shaped invariants: a series to plot, a sort it can apply, a breakdown to stack. */
function normalizeCharts(state: WorkspaceState, repairs: WorkspaceRepair[]): WorkspaceState {
	const charts = state.content.charts.flatMap((chart): [SmartWorkspaceDTOChart & { preferences?: object }] | [] => {
		if (chart.series.length === 0) {
			repairs.push({
				invariant: "chartHasSeries",
				detail: `dropped chart "${chart.label ?? chart.chartType}" — nothing left to plot`,
			});
			return [];
		}

		let normalized = chart;

		if (chart.preferences?.sort != null && chartTypeSortingMode(chart.chartType) === "NONE") {
			repairs.push({
				invariant: "chartSortOwnership",
				detail: `cleared the sort on chart "${chart.label ?? chart.chartType}" — a ${chart.chartType} cannot sort`,
			});
			normalized = { ...normalized, preferences: { ...normalized.preferences, sort: null } };
		}

		if (chart.chartType === "STACKED_COLUMN" && chart.breakdownProperties?.dimension == null) {
			// Downgraded rather than dropped: a stacked column with no dimension has one segment per portfolio,
			// which is a column chart — so this makes the document say what the chart was already drawing, and
			// keeps the series, the label and the legend the user configured. The chart panel would otherwise
			// open on it and refuse to submit ("Break a level down with “All” first") with nothing to fix.
			repairs.push({
				invariant: "stackedColumnHasBreakdown",
				detail: `chart "${chart.label ?? chart.chartType}" is a stacked column with no breakdown — shown as a column`,
			});
			normalized = { ...normalized, chartType: WorkspaceChartTypeEnum.Histogram };
		}

		return [normalized];
	});

	return { ...state, content: { ...state.content, charts } };
}
